// ============================================================
// TERROR ENGINE — THE RAIL DOCK  (one owner for the backdrop band)
// ------------------------------------------------------------
// caP'n, 2026-09-23: "Clicking on one thing at a time should minimize
// another" + "I want it able to be minimized into just 'Menu'" + "you
// can also move it around".
//
// Before this, four things lived in the right-side band and each one
// kept its OWN open/closed state: the Record (RoomConsole), zone chat
// (ChatSystem, a full-width slide-up at the bottom of the screen), the
// Friends pill + panel (IEFriends, floating bottom-right on top of the
// room's own option buttons) and MENU. Nothing knew about anything else,
// so they stacked on each other.
//
// Now ONE store answers three questions, and every piece of chrome reads it:
//
//   panel      "record" | "chat" | "friends" | null
//              — which band panel is open. ONE at a time: opening one
//                closes whichever was open. Tapping the open one closes it.
//   collapsed  bool — the whole band folded into a single "☰ MENU" pill.
//   pill       {x, y} | null — where the folded pill was dragged to.
//              null = its home slot (the MENU pill's usual spot).
//
// WHERE IT APPLIES
//   Desktop / landscape only, and only while a RAIL HOST is mounted
//   (Plaza, or any room wrapped in RoomOverlay). A host calls
//   useRailHost() once. Screens with no rail (the Shatterfield, full-screen
//   takeovers) see `active: false` and every component falls back to its
//   old standalone behaviour — the EXCEPTION LAW, not a special case.
//   Mobile keeps its bottom-left thumb chrome untouched.
//
// PERSISTENCE
//   collapsed + pill position are per-viewer conveniences → localStorage,
//   every access in try/catch. The open panel is NOT persisted: it resets
//   to the Record on reload, which is what the band looked like before.
// ============================================================

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { GM, OBSIDIAN_SLAB, facetTB, textGlow } from "./theme";

// ── Geometry — the one place the band's rows are measured ──────
export const RAIL_RIGHT = 12;
export const RAIL_W     = "min(280px, 24vw)";
export const RAIL_MENU_TOP = 12;    // MENU pill
export const RAIL_ROW_TOP  = 60;    // chat bubble · Friends · fold, one row
export const RAIL_ROW_H    = 44;
export const RAIL_SLOT_TOP = 112;   // first row a panel may start on
export const RAIL_SLOT_BOTTOM = 70; // leave the prompt pill its floor
export const RAIL_TAB_H    = 40;    // the Record's header pill + gap — it stays
                                    // as a TAB at the top of the slot while chat
                                    // or Friends is open below it

const LS_KEY = "terror.rail.v1";

function loadPersisted() {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw);
    return {
      collapsed: !!v.collapsed,
      pill: v.pill && Number.isFinite(v.pill.x) && Number.isFinite(v.pill.y) ? v.pill : null,
    };
  } catch { return {}; }
}
function persist() {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify({ collapsed: S.collapsed, pill: S.pill }));
  } catch { /* private window / blocked storage — the rail still works */ }
}

// ── The store ──────────────────────────────────────────────────
let S = {
  panel: "record",
  collapsed: false,
  pill: null,
  hosts: 0,
  hostTop: RAIL_SLOT_TOP,   // set by the screen hosting the band
  consoleTop: 0,            // set by RoomConsole while mounted (its topOffset)
  ...(typeof window !== "undefined" ? loadPersisted() : {}),
};
const listeners = new Set();
function emit(next) { S = { ...S, ...next }; listeners.forEach(l => l()); }
function subscribe(l) { listeners.add(l); return () => listeners.delete(l); }
const snap = () => S;

export const rail = {
  /** Open `id`, closing whatever else was open. Tapping the open one closes it. */
  toggle(id) { emit({ panel: S.panel === id ? null : id, collapsed: false }); persist(); },
  open(id)   { emit({ panel: id, collapsed: false }); persist(); },
  close(id)  { if (!id || S.panel === id) emit({ panel: null }); },
  collapse() { emit({ collapsed: true }); persist(); },
  expand()   { emit({ collapsed: false }); persist(); },
  setPill(p) { emit({ pill: p }); persist(); },
  setConsoleTop(t) { if (t !== S.consoleTop) emit({ consoleTop: t }); },
};

// ── Desktop test — same breakpoint RoomOverlay/Plaza already use ─
function isDesktop() {
  if (typeof window === "undefined") return false;
  const narrow = window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
  const portrait = window.innerHeight > window.innerWidth;
  return !narrow && !portrait;
}
function useDesktop() {
  const [d, setD] = useState(isDesktop);
  useEffect(() => {
    const on = () => setD(isDesktop());
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("orientationchange", on); };
  }, []);
  return d;
}

/**
 * useRail() → { active, panel, collapsed, pill, slotTop, ...rail }
 * `active` is true only on desktop with a rail host mounted.
 */
export function useRail() {
  const s = useSyncExternalStore(subscribe, snap, snap);
  const desktop = useDesktop();
  // Both writers are separate fields and the answer is their max, so it
  // never matters whose effect ran last (a child's effects fire BEFORE its
  // parent's — a single shared field would be won by the host every time).
  const slotTop = Math.max(s.hostTop, s.consoleTop);
  return { ...rail, ...s, slotTop, active: desktop && s.hosts > 0 };
}

/**
 * A screen that owns the band (Plaza, RoomOverlay) calls this once.
 * `slotTop` = the first y a band panel may start at on that screen.
 */
export function useRailHost(enabled = true, slotTop = RAIL_SLOT_TOP) {
  useEffect(() => {
    if (!enabled) return undefined;
    emit({ hosts: S.hosts + 1, hostTop: slotTop });
    return () => emit({ hosts: Math.max(0, S.hosts - 1) });
  }, [enabled, slotTop]);
}

/** Fixed box every band panel (Record body, chat, friends) fills. */
export function railPanelStyle(slotTop = Math.max(S.hostTop, S.consoleTop)) {
  return {
    position: "fixed",
    right: RAIL_RIGHT, width: RAIL_W,
    top: slotTop + RAIL_TAB_H, bottom: RAIL_SLOT_BOTTOM,
    left: "auto", maxHeight: "none",
  };
}

// ── The fold button — sits at the LEFT end of the button row ─────
export function RailFoldButton() {
  const r = useRail();
  if (!r.active || r.collapsed) return null;
  return (
    <button
      onClick={() => rail.collapse()}
      aria-label="Minimize the menu band"
      title="Minimize — fold everything into the Menu pill"
      style={{
        position: "fixed", zIndex: 70,
        top: RAIL_ROW_TOP + 6,
        right: `calc(${RAIL_RIGHT}px + ${RAIL_W} - 32px)`,
        width: 32, height: 32, borderRadius: "50%",
        background: "linear-gradient(175deg, rgba(5,3,12,0.95) 0%, rgba(13,9,24,0.92) 100%)",
        border: `1px solid ${GM.pageBorder}`,
        color: GM.inkGoldBright, fontFamily: GM.font, fontSize: 14, fontWeight: 900,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 14px rgba(0,0,0,0.6)",
      }}
    >—</button>
  );
}

// ── The folded pill — draggable, click to unfold ─────────────────
// A press that moves < 5px is a click; anything more is a drag. The
// position is clamped to the viewport on drop AND on every resize, so a
// pill parked in a corner can never end up off-screen after the window
// shrinks.
const DRAG_SLOP = 5;
function clampPill(p, w, h) {
  const vw = window.innerWidth, vh = window.innerHeight;
  return { x: Math.min(Math.max(4, p.x), vw - w - 4), y: Math.min(Math.max(4, p.y), vh - h - 4) };
}

export function RailMenuPill() {
  const r = useRail();
  const ref = useRef(null);
  const drag = useRef(null);
  const [live, setLive] = useState(null);   // position while dragging

  useEffect(() => {
    if (!r.active || !r.collapsed || !r.pill) return undefined;
    const on = () => {
      const el = ref.current; if (!el) return;
      const c = clampPill(S.pill, el.offsetWidth, el.offsetHeight);
      if (c.x !== S.pill.x || c.y !== S.pill.y) rail.setPill(c);
    };
    on();
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [r.active, r.collapsed, r.pill]);

  if (!r.active || !r.collapsed) return null;

  const onDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    const el = ref.current; if (!el) return;
    const b = el.getBoundingClientRect();
    drag.current = { sx: e.clientX, sy: e.clientY, ox: b.left, oy: b.top, moved: false, w: b.width, h: b.height };
    try { el.setPointerCapture(e.pointerId); } catch { /* old browser */ }
  };
  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
    d.moved = true;
    d.last = clampPill({ x: d.ox + dx, y: d.oy + dy }, d.w, d.h);
    setLive(d.last);
  };
  const onUp = (e) => {
    const d = drag.current; drag.current = null;
    try { ref.current && ref.current.releasePointerCapture(e.pointerId); } catch { /* fine */ }
    if (!d) return;
    if (d.moved) { if (d.last) rail.setPill(d.last); setLive(null); }
    else rail.expand();
  };

  const at = live || r.pill;
  const place = at
    ? { left: at.x, top: at.y }
    : { right: RAIL_RIGHT, top: RAIL_MENU_TOP };

  return (
    <button
      ref={ref}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={() => { drag.current = null; setLive(null); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); rail.expand(); } }}
      aria-label="Open the menu band (drag to move)"
      title="Click to open · drag to move"
      style={{
        position: "fixed", ...place, zIndex: 75,
        display: "flex", alignItems: "center", gap: 6,
        padding: "9px 18px",
        background: OBSIDIAN_SLAB,
        border: "none", borderTop: "1px solid #f5d878",
        clipPath: facetTB(9),
        color: "#ffe9a8", fontFamily: GM.font,
        fontSize: 12, fontWeight: 700, letterSpacing: 2,
        cursor: live ? "grabbing" : "grab",
        touchAction: "none",
        boxShadow: `inset 0 1px 0 rgba(245,216,120,0.5), inset 0 -6px 14px rgba(0,0,0,0.45), inset 0 0 16px ${GM.amethyst}22`,
        filter: `drop-shadow(0 4px 10px rgba(0,0,0,0.65)) drop-shadow(0 0 9px ${GM.inkGoldBright}44)`,
        textShadow: textGlow(GM.inkGoldBright),
        userSelect: "none", WebkitUserSelect: "none",
      }}
    >
      ☰ MENU
    </button>
  );
}

// ── Friends, seated in the band ──────────────────────────────────
// Mounted from index.js in place of the bare <IESocialDock/>. With a rail
// host on screen the pill sits in the button row beside the chat bubble
// and its panel opens in the band like any other rail panel. With no rail
// (the Shatterfield, mobile) every docking prop is left off, so the dock
// behaves exactly as it did before this file existed.
export function RailFriendsDock({ Dock, ...props }) {
  const r = useRail();
  if (!r.active) return <Dock {...props} />;
  return (
    <Dock
      {...props}
      hidePill={r.collapsed}
      open={!r.collapsed && r.panel === "friends"}
      onOpenChange={(v) => (v ? rail.open("friends") : rail.close("friends"))}
      pillStyle={{
        bottom: "auto", left: "auto",
        top: RAIL_ROW_TOP + 5, right: RAIL_RIGHT + RAIL_ROW_H + 8,
      }}
      panelStyle={railPanelStyle(r.slotTop)}
    />
  );
}
